import { useCallback, useEffect, useState } from 'react'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi
} from '~/components/UI/shadcn/carousel'

import { useMediaQuery } from 'usehooks-ts'

import { cn } from '~/lib/utils'
import CircleInfo from './icons/circle-info'
import CarouselControls from '~/components/UI/General/CarouselControls/CarouselControls'
import { ClientOnly } from 'remix-utils/client-only'
import ImageModal from './Modal/ImageModal'
// import Loader from '~/components/UI/General/Loader/Loader'
// import { isDevelopment } from '~/utils/misc'
import { Play } from 'lucide-react'
import BlockVideo from '~/components/Media/components/BlockVideo'
import Image from '~/components/Helpers/Image'

export interface ImageGalleryProps {
  content: {
    showCaptions?: boolean
    galleryStyle?: string
    images: Image[]
  }
  aspect?: string
  layoutColour?: string
  layoutStyle?: string
}

type Image = {
  image: {
    altText: string
    asset: { sys: { uri: string } }
    caption: string
  }
  video?: {
    sys: { uri: string }
  } | null
}

// const PLACEHOLDER_SRC = `data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs%3D`

const ImageGallery = ({
  content,
  aspect = 'image',
  layoutColour,
  layoutStyle
}: ImageGalleryProps) => {
  const { images = [], showCaptions, galleryStyle } = content

  const [api, setApi] = useState<CarouselApi>()
  const [thumbsApi, setThumbsApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [modalSlide, setModalSlide] = useState(0)
  const [caption, setCaption] = useState('')
  const [showCaption, setShowCaption] = useState(false)

  // const [slidesInView, setSlidesInView] = useState<number[]>([])
  // const [hasLoaded, setHasLoaded] = useState(false)

  const isMobile = useMediaQuery('(max-width: 767px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  // Sync main gallery's api with current slide
  useEffect(() => {
    if (api) {
      api.scrollTo(current) // Scroll the main gallery to the current slide
    }
  }, [current, api])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // const updateSlidesInView = useCallback((api: any) => {
  //   setSlidesInView(slidesInView => {
  //     if (slidesInView.length === api.slideNodes().length) {
  //       api.off('slidesInView', updateSlidesInView)
  //     }
  //     const inView = api
  //       .slidesInView()
  //       .filter((index: number) => !slidesInView.includes(index))
  //     return slidesInView.concat(inView)
  //   })
  // }, [])

  // useEffect(() => {
  //   if (!api) return

  //   updateSlidesInView(api)

  //   api.on('slidesInView', updateSlidesInView)
  //   api.on('reInit', updateSlidesInView)
  // }, [api, updateSlidesInView])

  // Set current slide on load
  useEffect(() => {
    // If no api, return
    if (!api) return
    if (galleryStyle === 'full' && (!api || !thumbsApi)) return

    const updateCarouselState = () => {
      setCurrent(api.selectedScrollSnap())
    }

    const updateThumbsState = () => {
      thumbsApi?.scrollTo(api.selectedScrollSnap())
    }

    // Update on load
    updateCarouselState()
    updateThumbsState()

    // Listen for select event
    api.on('select', updateCarouselState)
    api.on('select', updateThumbsState)

    return () => {
      // Remove event listener
      api.off('select', updateCarouselState)
      api.off('select', updateThumbsState)
    }
  }, [api, galleryStyle, thumbsApi])

  // Set caption on load
  useEffect(() => {
    if (images[current]) {
      setCaption(images[current].image.caption)
    }
  }, [current, images])

  // Set current slide on thumbnail click
  const onThumbClick = useCallback(
    (index: number) => {
      if (!api) return

      api.scrollTo(index)
      thumbsApi?.scrollTo(index)
    },
    [api, thumbsApi]
  )

  // const setLoaded = useCallback(() => {
  //   if (slidesInView) setHasLoaded(true)
  // }, [slidesInView, setHasLoaded])

  const onModalClose = () => {
    setCurrent(modalSlide)
  }

  return (
    <div className='max-sm:-ml-s max-sm:w-screen'>
      <div className='relative'>
        <Carousel
          setApi={setApi}
          className=''
          opts={{
            align: 'start',
            loop: galleryStyle === 'full' ? false : true
          }}
        >
          <CarouselContent
            className={
              aspect === 'image'
                ? layoutStyle === 'halves'
                  ? 'aspect-44/39'
                  : 'aspect-image'
                : 'aspect-square'
            }
          >
            {/* {!hasLoaded && <Loader fill={'#f5b6dc'} />} */}

            {images?.map((item, index) => (
              <CarouselItem key={`list-${index}`} className='relative !pl-0'>
                {item.video ? (
                  <BlockVideo
                    videoUrl={item.video.sys.uri}
                    width={''}
                    height={''}
                    image={item.image}
                    btnPosition='top-right'
                    btnClassName={cn(
                      '!right-m !top-m transition-all duration-500',
                      current !== index
                        ? 'invisible opacity-0'
                        : 'visible opacity-100'
                    )}
                    pause={current !== index}
                  />
                ) : (
                  <Image
                    image={item.image}
                    lazyLoad={index === 0 ? false : true}
                    priority={index === 0 ? 'high' : 'low'}
                  />
                  // <img
                  //   src={
                  //     slidesInView.indexOf(index) > -1
                  //       ? (isDevelopment
                  //           ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                  //           : '') +
                  //         item.image.asset.sys.uri +
                  //         (item.image.asset.sys.uri.includes('?') ? '&' : '?') +
                  //         'w=1000&h=1000&f=webp'
                  //       : PLACEHOLDER_SRC
                  //   }
                  //   alt={item.image.altText}
                  //   className='h-full w-full object-cover'
                  //   data-src={item.image.asset.sys.uri}
                  //   onLoad={setLoaded}
                  //   width={700}
                  //   height={700}
                  // />
                )}
              </CarouselItem>
            ))}
          </CarouselContent>

          {galleryStyle === 'minimal' && (
            <div className='p-xs md:p-s absolute right-0 bottom-0 z-10 flex w-fit'>
              <ClientOnly>
                {() => (
                  <CarouselControls
                    current={0}
                    count={images.length}
                    colour={'white'}
                    setCurrent={(index: number) => {
                      if (api) {
                        api.scrollTo(index) // Scroll to the selected slide
                        setCurrent(index) // Update the current state
                      }
                    }}
                    hidePager={true}
                  />
                )}
              </ClientOnly>
            </div>
          )}
        </Carousel>

        {showCaptions && caption && (
          <div
            className={cn(
              showCaption
                ? 'pt-l bg-gradient-to-t from-black/60 to-black/0'
                : '',
              'group hover:pt-l absolute bottom-0 flex w-full items-end justify-between pt-0 transition-all duration-300 ease-in-out hover:bg-gradient-to-t hover:from-black/60 hover:to-black/0'
            )}
          >
            <div className='flex grow items-end'>
              <div
                className='size-s m-xs md:m-s text-blue-d flex shrink-0 items-center justify-center rounded-full'
                onTouchStart={() => {
                  setShowCaption(!showCaption)
                }}
              >
                <CircleInfo />
              </div>
              <div
                className={cn(
                  galleryStyle === 'minimal' ? 'pr-3xl' : '',
                  'py-xs md:py-s max-w-[75ch] text-pretty text-white italic opacity-0 transition-opacity duration-300 group-hover:opacity-100',
                  showCaption ? 'opacity-100' : 'opacity-0'
                )}
              >
                {caption}
              </div>
            </div>
          </div>
        )}
      </div>
      {galleryStyle === 'full' && (
        <div
          className={cn(
            ['light-grey'].includes(layoutColour ?? '')
              ? 'bg-white'
              : 'bg-grey-l',
            'p-xs-plus sm:p-s flex items-center justify-between'
          )}
        >
          <Carousel
            setApi={setThumbsApi}
            opts={{ align: 'center', containScroll: 'keepSnaps' }}
            className='relative flex items-center overflow-x-auto'
          >
            <CarouselContent className='mr-s -ml-xs sm:-ml-s h-[40px]'>
              {images?.map((item, index: Index) => (
                <CarouselItem
                  key={`thumb-${index}`}
                  className='pl-xs sm:pl-s first:pl-xs first:sm:pl-s basis-auto'
                >
                  <button
                    key={`thumb-${index}`}
                    onClick={() => {
                      onThumbClick(index)
                      // setIsVisible(item.video ? true : false)
                    }}
                    type='button'
                    className={cn(
                      'relative size-[40px] h-fit shrink-0 cursor-pointer overflow-hidden transition-opacity duration-300',
                      `${index === current ? 'opacity-100' : 'opacity-50'}`
                    )}
                  >
                    {item.video && (
                      <div className='size-xs bg-char absolute bottom-0 flex items-center justify-center'>
                        <Play className='text-blue-d size-2xs-plus' />
                      </div>
                    )}

                    <Image image={item.image} />
                    {/* <img
                      src={
                        (isDevelopment
                          ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                          : '') +
                        item.image.asset.sys.uri +
                        (item.image.asset.sys.uri.includes('?') ? '&' : '?') +
                        'w=100&h=100&f=webp'
                      }
                      alt={item.image.altText}
                      className='h-full w-full object-cover'
                      loading='lazy'
                      width={50}
                      height={50}
                    /> */}
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {!isMobile && (
            <div
              className={cn(
                'pl-3xs relative h-full',
                'before:absolute before:top-0 before:right-0 before:z-[0] before:h-full before:w-[calc(100%+50px)] before:content-[""]',
                'before:bg-linear-to-r before:to-20%',
                ['light-grey'].includes(layoutColour ?? '')
                  ? 'before:from-white/0 before:to-white'
                  : 'before:from-grey-l/0 before:to-grey-l'
              )}
            >
              <ImageModal
                images={images}
                selectedSlide={current}
                onSlideChange={index => setModalSlide(index)}
                onClose={onModalClose}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ImageGallery
